/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  DELETE_COLOR,
  GRAY_COLOR,
  PRIMARY,
  WATSAPP_ACTIVE
} from "@constants/colors"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import VisibilityIcon from "@mui/icons-material/Visibility"
import Box from "@mui/material/Box"
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowModel
} from "@mui/x-data-grid"
import { formatDate } from "@utils/functions/formatDate"
import { useMemo, useState } from "react"

import DataTableToolbar from "@components/data_table_toolbar"
import CustomerAccessAccount from "@components/dialogs/customer_acess_account"
import DeleteCustomer from "@components/dialogs/delete_customer"
import GridTable from "@components/grid-table"
import { useCustomerPageContext } from "@contexts/customer"
import { deleteCustomerMutation } from "@domain/customer/infos"
import { IconButton, Popover, Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { RoleTypes } from "__generated__/graphql"
import { useNavigate } from "react-router-dom"
import CustomerFilters from "./sub_components/customer_filters"
import styles from "./styles"

export type DataTableRow = {
  _id: string
  company: string
  owner: string
  customer: string
  email: string
  phone: string
  mainPlan: string
  userCount: string
  renewalType: string
  isEmailVerified: string
  appStatus: string
  accountStatus: string
  allowConnect: boolean
  createdAt: string
}

export default function CustomerDataTable() {
  const {
    me,
    translate,
    customerData,
    search,
    setSearch,
    isLoading,
    pagination,
    setPagination,
    selectedCustomerId,
    setSelectedCustomerId,
    toggleDeleteCustomerDialogOpen
  } = useCustomerPageContext()

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  function handleCompanyClick(id: string) {
    navigate(`/customers/${id}`)
  }

  const [isVerifyOpen, setIsVerifyOpen] = useState(false)
  const [clickedRow, setClickedRow] = useState<DataTableRow>()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const [selectedRows, setSelectedRows] = useState<GridRowModel[]>([])

  const canUserFetchCustomers = useMemo(() => {
    if (me?.role === RoleTypes.Admin) {
      return true
    }

    return me?.role === RoleTypes.User && search !== ""
  }, [me?.role, search])

  const handleClickRightIcon = (
    event: React.MouseEvent<HTMLButtonElement>,
    params: GridRenderCellParams
  ) => {
    params.id as string
    setAnchorEl(event.currentTarget)
    setSelectedCustomerId?.(params.id as string)
  }

  async function handleDeleteCustomers() {
    setAnchorEl(null)

    const isMultipleCustomers = selectedRows.length > 1
    if (isMultipleCustomers) {
      return
    }

    //todo: handle the case when multiple customers are deleted
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await deleteCustomerMutation({
      customerId:
        selectedRows.length > 0
          ? selectedRows[0].customer._id
          : selectedCustomerId
    })
    if (!isSuccess || !responseData) {
      showErrorToast(String(errorMessage))
      return
    }

    queryClient.invalidateQueries({ queryKey: ["customer-data-table"] })

    // toggleDeleteCustomerDialogOpen?.()
  }

  const handleVerifyOpen = (row: DataTableRow) => {
    setIsVerifyOpen(true)
    setClickedRow(row)
  }

  const handleVerifyClose = () => {
    setIsVerifyOpen(false)
  }

  const fieldWidth = 100

  const columns: GridColDef[] = [
    {
      field: "company",
      minWidth: 150,
      headerName: translate("company"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("company")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box
            sx={styles.renderCellIconBox}
            onClick={() => handleCompanyClick(params.id as string)}
          >
            {params.value ? params.value : "-"}
          </Box>
        )
      },
      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "owner",
      minWidth: 200,
      headerName: translate("owner"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("owner")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as DataTableRow
        return (
          <Stack flexDirection={"row"} gap={1}>
            <Box
              onClick={
                row?.allowConnect ? () => handleVerifyOpen(row) : undefined
              }
            >
              <VisibilityIcon
                color="primary"
                sx={{
                  marginBottom: -1,
                  cursor: "pointer",
                  color: row.allowConnect ? PRIMARY : GRAY_COLOR
                }}
              />
            </Box>
            <Box
              sx={styles.renderCellIconBox}
              onClick={() => handleCompanyClick(params.id as string)}
            >
              {params.value}
            </Box>
          </Stack>
        )
      },
      disableColumnMenu: true
    },
    {
      field: "email",
      minWidth: 300,
      headerName: translate("owner_email"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("owner_email")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>{params.value as string}</Box>
      ),
      disableColumnMenu: true
    },
    {
      field: "phone",
      minWidth: 150,
      headerName: translate("owner_phone"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("owner_phone")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>{params.value as string}</Box>
      ),
      disableColumnMenu: true
    },
    {
      field: "mainPlan",
      minWidth: fieldWidth,
      headerName: translate("main_plan"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("main_plan")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>{params.value ? params.value : "-"}</Box>
      ),
      disableColumnMenu: true
    },
    {
      field: "renewalType",
      minWidth: fieldWidth,
      headerName: translate("renewal_type"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("renewal_type")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>{params.value ? params.value : "-"}</Box>
      ),
      disableColumnMenu: true
    },
    {
      field: "isEmailVerified",
      minWidth: fieldWidth,
      headerName: translate("email_status"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("email_status")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: params.value === true ? WATSAPP_ACTIVE : DELETE_COLOR
          }}
        >
          {params.value ? translate("verified") : translate("unverified")}
        </Box>
      ),
      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "appStatus",
      minWidth: fieldWidth,
      headerName: translate("app_status"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("app_status")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: params.value === "Enabled" ? WATSAPP_ACTIVE : DELETE_COLOR
          }}
        >
          {params.value?.charAt(0) + params.value?.substring(1).toLowerCase()}
        </Box>
      ),
      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "accountStatus",
      minWidth: fieldWidth,
      headerName: translate("account_status"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("account_status")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: params.value === "Active" ? WATSAPP_ACTIVE : DELETE_COLOR
          }}
        >
          {params.value?.charAt(0) + params.value?.substring(1).toLowerCase()}
        </Box>
      ),
      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "createdAt",
      minWidth: fieldWidth,
      headerName: translate("created_at"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("created_at")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {formatDate(translate("date_format"), params.value as string)}
        </Box>
      ),
      disableColumnMenu: true,
      flex: 1,
      align: "right",
      headerAlign: "right"
    },
    {
      field: "rightIcon",
      minWidth: 100,
      renderHeader: () => <Box></Box>,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box>
            <IconButton
              onClick={(event) => handleClickRightIcon(event, params)}
            >
              <MoreVertIcon sx={{ cursor: "pointer" }} />
            </IconButton>
          </Box>
        )
      },
      sortable: false,
      disableColumnMenu: true,
      hideSortIcons: true,
      align: "right",
      headerAlign: "right"
    }
  ]

  return (
    <Stack sx={{ overflow: "hidden", height: "100%", p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DataTableToolbar
          searchText={search}
          onSearch={setSearch}
          concatProps={{
            rowLength: selectedRows.length,
            toggleDeleteCustomerDialogOpen
          }}
        />
        <CustomerFilters />
      </Box>
      <GridTable
        sortable
        loading={isLoading}
        rows={customerData?.docs?.map((item) => item.customerPerson)}
        columns={columns}
        rowCount={customerData?.totalDocs || 0}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row) => row.customer}
        disableColumnSelector={true}
        noRowsTableText={
          canUserFetchCustomers
            ? "no_data_found"
            : "can_not_search_all_customers"
        }
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        sx={{
          "& > *": {
            borderRadius: 2.3,
            boxShadow: "0px 2px 8px #00000029"
          }
        }}
      >
        <Box
          sx={{
            ...styles.popOverOption,
            color: me?.role === RoleTypes.User ? "grey" : "red",
            pointerEvents: me?.role === RoleTypes.User ? "none" : "auto", // prevents clicks
            opacity: me?.role === RoleTypes.User ? 0.5 : 1, // visual feedback
            cursor: me?.role === RoleTypes.User ? "not-allowed" : "pointer"
          }}
          onClick={toggleDeleteCustomerDialogOpen}
        >
          {translate("delete")}
        </Box>
      </Popover>
      <CustomerAccessAccount
        isOpen={isVerifyOpen}
        setClose={handleVerifyClose}
        selectedRowId={clickedRow?.customer}
      />
      {/* <Delete Customer Dialog /> */}
      <DeleteCustomer />
    </Stack>
  )
}
